import React, { useState, useEffect, useContext } from 'react';
import { View, Text, StyleSheet, ScrollView, Dimensions, ActivityIndicator, RefreshControl } from 'react-native';
import { LineChart, RadarChart, PieChart } from 'react-native-chart-kit';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import Colors from '../constants/Colors';
import { ScoreCard } from '../components/ui/ScoreCard';

const theme = Colors.light;
const { spacing, radius } = Colors;
const screenWidth = Dimensions.get('window').width;

const chartConfig = {
    backgroundGradientFrom: theme.card,
    backgroundGradientTo: theme.card,
    color: (opacity = 1) => `rgba(255, 45, 85, ${opacity})`, // Primary color
    labelColor: (opacity = 1) => `rgba(60, 60, 67, ${opacity})`,
    strokeWidth: 2,
    barPercentage: 0.5,
    useShadowColorFromDataset: false,
    propsForDots: {
        r: "4",
        strokeWidth: "2",
        stroke: theme.primary
    }
};

export default function StatsScreen() {
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [period, setPeriod] = useState('week'); // week, month, year
    const [data, setData] = useState({ trend: [], radar: [], contribution: [] });

    const loadStats = async (selectedPeriod = period) => {
        try {
            setRefreshing(true);
            const res = await api.get(`/score/stats?period=${selectedPeriod}`);
            setData(res.data);
        } catch (error) {
            console.log('Stats error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadStats(period);
    }, [period]);

    const onRefresh = () => {
        loadStats(period);
    };

    // 1. Process Trend Data for LineChart
    const processTrendData = () => {
        const labels = [];
        const myData = [];
        const partnerData = [];

        // If empty, return dummy data
        if (!data.trend || data.trend.length === 0) {
            return {
                labels: ['无数据'],
                datasets: [
                    { data: [0], color: () => theme.primary },
                    { data: [0], color: () => theme.secondary }
                ]
            };
        }

        data.trend.forEach(item => {
            // Format label based on period
            let label = item.date;
            if (period === 'year') {
                label = item.date.slice(5); // 'MM' (from YYYY-MM)
            } else {
                label = item.date.slice(5); // 'MM-DD' (from YYYY-MM-DD)
            }
            labels.push(label);
            myData.push(item.myScore || 0);
            partnerData.push(item.partnerScore || 0);
        });

        return {
            labels,
            datasets: [
                {
                    data: myData,
                    color: (opacity = 1) => `rgba(255, 45, 85, ${opacity})`, // Me (Pink)
                    strokeWidth: 2
                },
                {
                    data: partnerData,
                    color: (opacity = 1) => `rgba(88, 86, 214, ${opacity})`, // Partner (Purple)
                    strokeWidth: 2
                }
            ],
            legend: ['我', 'TA']
        };
    };

    // ... (radar and contribution processing remain similar, acting on filtered data from backend)
    // 2. Process Radar Data - No change needed logic-wise, just uses data.radar
    // 3. Process Contribution Data - No change needed logic-wise, uses data.contribution

    // ... (Contribution Pie logic unchanged)
    const processContributionData = () => {
        let myCount = 0;
        let partnerCount = 0;

        (data.contribution || []).forEach(item => { // Safe access
            if (item.receiver_id === user.id) partnerCount += item.count;
            if (item.receiver_id === user.partnerId) myCount += item.count;
        });

        if (myCount === 0 && partnerCount === 0) return [];

        return [
            {
                name: '我做了',
                population: myCount,
                color: theme.primary,
                legendFontColor: theme.textSecondary,
                legendFontSize: 12
            },
            {
                name: 'TA做了',
                population: partnerCount,
                color: theme.secondary,
                legendFontColor: theme.textSecondary,
                legendFontSize: 12
            }
        ];
    };

    // Filters and Constants
    const allCategories = ['romance', 'housework', 'emotion', 'growth', 'other'];
    const categoryLabels = {
        'romance': '浪漫',
        'housework': '家务',
        'emotion': '情绪',
        'growth': '成长',
        'other': '其他'
    };

    const periods = [
        { key: 'week', label: '本周' },
        { key: 'month', label: '本月' },
        { key: 'year', label: '本年' },
    ];

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={theme.primary} />
            </View>
        );
    }

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={{ paddingBottom: 100 }}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
            <View style={styles.header}>
                <Text style={styles.largeTitle}>数据统计</Text>
                <Text style={styles.subtitle}>看看谁是今日最佳。</Text>
            </View>

            {/* Period Selector */}
            <View style={styles.periodContainer}>
                {periods.map((p) => (
                    <Text
                        key={p.key}
                        style={[styles.periodTab, period === p.key && styles.periodTabActive]}
                        onPress={() => setPeriod(p.key)}
                    >
                        {p.label}
                    </Text>
                ))}
            </View>

            {/* Trend Chart */}
            <View style={styles.card}>
                <Text style={styles.cardTitle}>📅 得分趋势</Text>
                <LineChart
                    data={processTrendData()}
                    width={screenWidth - spacing.xl * 2 - spacing.lg * 2}
                    height={220}
                    chartConfig={{
                        ...chartConfig,
                        decimalPlaces: 0,
                        propsForLabels: { fontSize: 10 },
                    }}
                    bezier
                    style={styles.chart}
                    fromZero
                />
            </View>

            {/* Contribution Pie */}
            <View style={styles.card}>
                <Text style={styles.cardTitle}>🍰 贡献占比 (次数)</Text>
                {processContributionData().length > 0 ? (
                    <PieChart
                        data={processContributionData()}
                        width={screenWidth - spacing.xl * 2}
                        height={200}
                        chartConfig={chartConfig}
                        accessor={"population"}
                        backgroundColor={"transparent"}
                        paddingLeft={"15"}
                        center={[10, 0]}
                        absolute
                    />
                ) : (
                    <Text style={styles.noDataText}>暂无数据</Text>
                )}
            </View>

            {/* Category Breakdown */}
            <View style={styles.card}>
                <Text style={styles.cardTitle}>🏷️ 分类表现</Text>
                <View style={styles.categoryList}>
                    {allCategories.map(cat => {
                        const row = (data.radar || []).find(r => r.category === cat) || { myScore: 0, partnerScore: 0 };
                        // Calculate flex carefully to avoid crash if 0
                        const total = (row.myScore || 0) + (row.partnerScore || 0);
                        const maxVal = 100; // arbitrary max for bar width visualization if relative
                        // Or just show number. Bar needs flex.
                        return (
                            <View key={cat} style={styles.categoryRow}>
                                <Text style={styles.catLabel}>{categoryLabels[cat]}</Text>
                                <View style={styles.catBars}>
                                    <View style={styles.barRow}>
                                        <View style={[styles.bar, { flex: Math.max(row.myScore || 0, 0.1), backgroundColor: theme.primary }]} />
                                        <Text style={styles.barVal}>{row.myScore || 0}</Text>
                                    </View>
                                    <View style={styles.barRow}>
                                        <View style={[styles.bar, { flex: Math.max(row.partnerScore || 0, 0.1), backgroundColor: theme.secondary }]} />
                                        <Text style={styles.barVal}>{row.partnerScore || 0}</Text>
                                    </View>
                                </View>
                            </View>
                        );
                    })}
                </View>
            </View>

            <View style={{ height: spacing.xxxl }} />
        </ScrollView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.background,
        paddingHorizontal: spacing.xl,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    header: {
        marginTop: spacing.xxxl,
        marginBottom: spacing.lg,
    },
    largeTitle: {
        fontSize: 34,
        fontWeight: '700',
        color: theme.text,
        letterSpacing: 0.4,
    },
    subtitle: {
        fontSize: 15,
        color: theme.textSecondary,
        marginTop: spacing.xs,
        letterSpacing: -0.2,
    },
    card: {
        backgroundColor: theme.card,
        borderRadius: radius.card,
        padding: spacing.lg,
        marginBottom: spacing.lg,
        ...theme.shadowCard,
    },
    cardTitle: {
        fontSize: 17,
        fontWeight: '600',
        color: theme.text,
        marginBottom: spacing.lg,
        letterSpacing: -0.3,
    },
    // Filters
    periodContainer: {
        flexDirection: 'row',
        backgroundColor: theme.card,
        borderRadius: radius.md,
        padding: 4,
        marginBottom: spacing.lg,
        ...theme.shadowCard,
    },
    periodTab: {
        flex: 1,
        textAlign: 'center',
        paddingVertical: 8,
        fontSize: 14,
        fontWeight: '500',
        color: theme.textSecondary,
        borderRadius: radius.sm,
        overflow: 'hidden',
    },
    periodTabActive: {
        backgroundColor: theme.primary,
        color: '#fff',
        fontWeight: '600',
    },
    chart: {
        marginVertical: 8,
        borderRadius: 16,
    },
    noDataText: {
        textAlign: 'center',
        color: theme.muted,
        marginVertical: 20,
    },
    categoryList: {
        gap: spacing.md,
    },
    categoryRow: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 36,
    },
    catLabel: {
        width: 50,
        fontSize: 14,
        fontWeight: '600',
        color: theme.textSecondary,
    },
    catBars: {
        flex: 1,
        flexDirection: 'column',
        gap: 4,
    },
    barRow: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 14,
        gap: 8,
    },
    bar: {
        height: '100%',
        borderRadius: 4,
        minWidth: 4,
    },
    barVal: {
        fontSize: 10,
        color: theme.textMuted,
        width: 24,
    },
});
